import { BehaviorSubject } from 'rxjs'

import { DatasetId } from '@/infra/datasetCache/datasetPolicies'

export interface DatasetFreshnessEntry {
  /** Quando o dado em uso foi efetivamente carregado da origem. */
  loadedAt: Date
  /**
   * `true` quando o dado em uso está além do TTL rígido e a rede falhou — ou seja, o app está
   * mostrando dado aeronáutico vencido porque não teve alternativa.
   */
  isDegraded: boolean
}

export type DatasetFreshnessStateProps = Partial<Record<DatasetId, DatasetFreshnessEntry>>

/**
 * Frescor dos datasets de referência em uso.
 *
 * Existe para o app poder **avisar** quando está exibindo dado aeronáutico vencido. Dado de voo velho
 * apresentado como atual é risco de segurança; por outro lado, esconder a camada cria falso negativo
 * ("não há NOTAM" quando o correto é "não sabemos"). A política é mostrar sempre, com a idade
 * explícita — nunca esconder, nunca apagar.
 *
 * Subject em módulo (e não `BaseState` no DI) porque quem publica são repositórios, que rodam fora do
 * React, e quem lê é um presenter. Mesmo padrão do `LoadingState` e do `ScreenElementsState`.
 */
export const DatasetFreshnessState = new BehaviorSubject<DatasetFreshnessStateProps>({})

export const DatasetFreshnessStateMutator = {
  report: (id: DatasetId, entry: DatasetFreshnessEntry) => {
    DatasetFreshnessState.next({ ...DatasetFreshnessState.getValue(), [id]: entry })
  },

  clear: (id: DatasetId) => {
    const next = { ...DatasetFreshnessState.getValue() }
    delete next[id]
    DatasetFreshnessState.next(next)
  },

  reset: () => DatasetFreshnessState.next({})
}

/** O dataset vencido mais antigo em uso, ou `null` quando nada está degradado. */
export function getOldestDegradedDataset(state: DatasetFreshnessStateProps): DatasetFreshnessEntry | null {
  const degraded = Object.values(state).filter((entry): entry is DatasetFreshnessEntry => Boolean(entry?.isDegraded))

  if (degraded.length === 0) return null

  return degraded.reduce((oldest, entry) => (entry.loadedAt < oldest.loadedAt ? entry : oldest))
}
